import { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/auth';
import NewsModal from './NewsModal';

const api = axios.create({ baseURL: '/api', timeout: 15000 });
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function NewsCard({ item, featured, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', gap: 14, padding: featured ? '16px 0' : '13px 0',
        borderBottom: '1px solid var(--border)', textDecoration: 'none',
        transition: 'opacity 0.15s', cursor: 'pointer',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      {item.thumbnail && (
        <img
          src={item.thumbnail}
          alt=""
          style={{ width: featured ? 90 : 70, height: featured ? 60 : 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
          onError={e => e.target.style.display = 'none'}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: featured ? 14 : 13, fontWeight: featured ? 700 : 500,
          color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {item.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{item.publisher}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(item.publishedAt)}</span>
          {item.tickers?.length > 0 && (
            <>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>·</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>
                {item.tickers.slice(0, 3).join(' ')}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NewsTab({ symbol }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const url = symbol ? `/market/news/${symbol}` : '/market/news';
    api.get(url, auth())
      .then(r => setNews(r.data || []))
      .catch(() => setError('Could not load news'))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return (
    <div style={{ padding: '16px 0' }}>
      {[1,2,3,4,5].map(i => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ width: 70, height: 48, borderRadius: 6, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 14, borderRadius: 4, marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 14, borderRadius: 4, width: '70%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 10, borderRadius: 4, width: '30%' }} />
          </div>
        </div>
      ))}
    </div>
  );

  if (error) return <div style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>{error}</div>;
  if (!news.length) return <div style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>No news found</div>;

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textAlign: 'right' }}>
        Via Yahoo Finance · {news.length} articles · click to read
      </div>
      {news.map((item, i) => (
        <NewsCard key={item.id || i} item={item} featured={i === 0} onClick={() => setSelected(item)} />
      ))}
      {selected && <NewsModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
